import * as React from 'react'
import { Loader2, Plus, Video } from 'lucide-react'
import type { TreeProblemTypeDto } from '@lib/dto/tree-problem-type.dto'
import { createTreeProblemTypeInputSchema } from '@lib/input/tree-problem-type.input'
import {
  useArchiveTreeProblemType,
  useCreateTreeProblemType,
  useTreeProblemTypes,
  useUpdateTreeProblemType,
} from '@/hooks/useTreeProblemTypes'
import { StockPhotoField, StockPhotoThumb } from '@/components/StockPhotoField'
import { useArchiveVideo } from '@/hooks/useVideos'
import logger, { errorMessage, toErrorMessage } from '@/helpers/logger'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ProblemTypeVideo } from '@/components/ProblemTypeVideo'
import { ProblemSolutionsEditor } from '@/components/ProblemSolutionsEditor'
import { cn } from '@/lib/utils'

/**
 * The long-text control.
 *
 * The same class string three other pages write inline for their own
 * textareas. Named here rather than repeated a fourth time, because this file
 * needs two of them.
 */
const TEXTAREA_CLASS =
  'flex w-full rounded-md border border-default bg-surface px-2.5 py-1.5 text-sm text-primary-text transition-colors placeholder:text-disabled-text hover:border-strong focus-visible:outline-none'

/**
 * The problems a surveyor can choose from, and what to say about each.
 *
 * `description` and `solution` both reach a CLIENT in a proposal, which is why
 * the form says so rather than leaving somebody to write field shorthand into
 * a paragraph a HOA board will read. A stock photograph stands in when nobody
 * managed to photograph the problem on the actual tree.
 */
export function TreeProblemSettings() {
  const problemTypes = useTreeProblemTypes()
  const createType = useCreateTreeProblemType()
  const updateType = useUpdateTreeProblemType()
  const archiveType = useArchiveTreeProblemType()

  const [isAdding, setIsAdding] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [openSolutionsId, setOpenSolutionsId] = React.useState<string | null>(
    null,
  )
  const [error, setError] = React.useState<string | null>(null)

  const rows = problemTypes.data ?? []

  if (problemTypes.isPending) {
    return (
      <section className="rounded-lg border border-default bg-surface p-4">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
        </div>
      </section>
    )
  }

  if (problemTypes.isError) {
    return (
      <section className="rounded-lg border border-default bg-surface p-4 text-center">
        <p className="text-sm font-medium">Problems could not be loaded</p>
        <p className="mt-1 text-sm text-muted-text">
          Check your connection and try again.
        </p>
      </section>
    )
  }

  async function handleArchiveToggle(entry: TreeProblemTypeDto): Promise<void> {
    setError(null)
    try {
      await archiveType.mutateAsync({
        id: entry.id,
        archived: entry.archivedAt === null,
      })
    } catch (caught) {
      setError(errorMessage(caught, 'That could not be saved.'))
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Tree problems</h2>
          <p className="mt-0.5 text-sm text-muted-text">
            What a surveyor can flag on a tree, and what a client is told about
            it. The description appears in proposals, so it is worth writing in
            plain language rather than field shorthand. What is done about it
            comes from the solutions listed under each problem.
          </p>
        </div>
        {isAdding ? null : (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => {
              setError(null)
              setEditingId(null)
              setIsAdding(true)
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            Add problem
          </Button>
        )}
      </div>

      {error === null ? null : <p className="text-sm text-danger">{error}</p>}

      {isAdding ? (
        <div className="rounded-md border border-default bg-subtle px-3 py-3">
          <ProblemForm
            initial={{
              name: '',
              description: '',
              solution: '',
              photoDocumentId: null,
              videoId: null,
            }}
            isSaving={createType.isPending}
            submitLabel="Add problem"
            onCancel={() => {
              setIsAdding(false)
            }}
            onSubmit={async (draft) => {
              setError(null)
              const parsed = createTreeProblemTypeInputSchema.safeParse(draft)
              if (!parsed.success) {
                setError(parsed.error.issues[0]?.message ?? 'Check the details')
                return
              }
              try {
                await createType.mutateAsync(parsed.data)
                setIsAdding(false)
              } catch (caught) {
                setError(errorMessage(caught, 'That could not be saved.'))
              }
            }}
          />
        </div>
      ) : null}

      {rows.length === 0 ? (
        <p className="rounded-md border border-default px-3 py-6 text-center text-sm text-muted-text">
          No problems catalogued yet. A surveyor can still flag one on a tree
          and describe it themselves — what is listed here is what they can
          pick without typing.
        </p>
      ) : (
      <ul className="divide-y divide-default rounded-md border border-default">
        {rows.map((entry) => (
          <li key={entry.id}>
            <div
              className={cn(
                'flex items-center gap-3 px-3 py-2',
                entry.archivedAt === null ? null : 'opacity-55',
              )}
            >
              <StockPhotoThumb documentId={entry.photoDocumentId} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {entry.name}
                  {entry.archivedAt === null ? null : (
                    <span className="ml-2 text-xs font-normal text-muted-text">
                      Archived
                    </span>
                  )}
                </span>
                {entry.description === null ? null : (
                  <span className="mt-0.5 block truncate text-xs text-muted-text">
                    {entry.description}
                  </span>
                )}
                {entry.videoId === null ? null : (
                  <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-text">
                    <Video className="h-3 w-3" />
                    Has an explainer video
                  </span>
                )}
              </span>
              {/*
                Doubles as the disclosure: the count IS the reason to open it,
                and a separate chevron beside a number that says the same thing
                is two controls for one intention.
              */}
              <Button
                size="sm"
                variant="ghost"
                className="shrink-0"
                onClick={() => {
                  setEditingId(null)
                  setOpenSolutionsId(
                    openSolutionsId === entry.id ? null : entry.id,
                  )
                }}
              >
                {(() => {
                  const live = entry.solutions.filter(
                    (solution) => solution.archivedAt === null,
                  ).length
                  return live === 0
                    ? "No solutions"
                    : `${String(live)} ${live === 1 ? "solution" : "solutions"}`
                })()}
              </Button>
              {entry.archivedAt === null ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setError(null)
                    // Closes the solutions panel, as opening that one
                    // closes this. Both open at once is two editors stacked
                    // on one row. Add is closed too: the two forms share one
                    // field id, so a label would focus the wrong one.
                    setOpenSolutionsId(null)
                    setIsAdding(false)
                    setEditingId(editingId === entry.id ? null : entry.id)
                  }}
                >
                  Edit
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                disabled={archiveType.isPending}
                onClick={() => {
                  void handleArchiveToggle(entry)
                }}
              >
                {entry.archivedAt === null ? 'Archive' : 'Restore'}
              </Button>
            </div>

            {openSolutionsId === entry.id ? (
              <ProblemSolutionsEditor problemType={entry} />
            ) : null}
            {editingId === entry.id ? (
              <div className="border-t border-default bg-subtle px-3 py-3">
                <ProblemForm
                  initial={{
                    name: entry.name,
                    description: entry.description ?? '',
                    solution: entry.solution ?? '',
                    photoDocumentId: entry.photoDocumentId,
                    videoId: entry.videoId,
                  }}
                  isSaving={updateType.isPending}
                  submitLabel="Save changes"
                  onCancel={() => {
                    setEditingId(null)
                  }}
                  onSubmit={async (draft) => {
                    setError(null)
                    try {
                      await updateType.mutateAsync({
                        id: entry.id,
                        input: {
                          name: draft.name,
                          description: draft.description,
                          solution: draft.solution,
                          photoDocumentId: draft.photoDocumentId,
                          videoId: draft.videoId,
                        },
                      })
                      setEditingId(null)
                    } catch (caught) {
                      setError(errorMessage(caught, 'That could not be saved.'))
                    }
                  }}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      )}
    </section>
  )
}

interface ProblemDraft {
  name: string
  description: string
  solution: string
  photoDocumentId: string | null
  videoId: string | null
}

/**
 * Throw away a clip this form uploaded and never saved.
 *
 * Only when it differs from what the entry already had: canceling an edit
 * must not delete the clip that was there before the form was opened. Hosted
 * video is billed by stored minutes, so a clip uploaded into a form somebody
 * then abandoned is a standing charge for something nothing points at.
 */
function ProblemForm({
  initial,
  isSaving,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial: ProblemDraft
  isSaving: boolean
  submitLabel: string
  onCancel: () => void
  onSubmit: (draft: ProblemDraft) => Promise<void>
}) {
  const [draft, setDraft] = React.useState<ProblemDraft>(initial)
  const discardVideo = useArchiveVideo(null)
  const [uploading, setUploading] = React.useState(false)

  return (
    <form
      className="space-y-3"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        void onSubmit(draft)
      }}
    >
      <div className="max-w-xs space-y-1.5">
        <Label htmlFor="problem-name">Name</Label>
        <Input
          id="problem-name"
          value={draft.name}
          placeholder="Mistletoe"
          onChange={(event) => {
            setDraft((current) => ({ ...current, name: event.target.value }))
          }}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="problem-description">What it is</Label>
        <textarea
          id="problem-description"
          rows={3}
          className={TEXTAREA_CLASS}
          value={draft.description}
          placeholder="A parasitic plant that roots into the branch and draws water from the host…"
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setDraft((current) => ({
              ...current,
              description: event.target.value,
            }))
          }}
        />
        <p className="text-xs text-muted-text">
          Shown to the client in a proposal. Say what it is and why it matters.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="problem-solution">What we do about it</Label>
        <textarea
          id="problem-solution"
          rows={3}
          className={TEXTAREA_CLASS}
          value={draft.solution}
          placeholder="Prune infected limbs back to the collar, which slows the spread…"
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setDraft((current) => ({
              ...current,
              solution: event.target.value,
            }))
          }}
        />
        <p className="text-xs text-muted-text">
          No longer shown to clients: a proposal lists the solutions chosen for
          each problem instead. Kept for now, as a note for your own reference.
        </p>
      </div>

      <StockPhotoField
        documentId={draft.photoDocumentId}
        onChange={(photoDocumentId) => {
          setDraft((current) => ({ ...current, photoDocumentId }))
        }}
        onBusyChange={setUploading}
        hint="Stands in when a surveyor could not photograph the problem on the tree itself."
      />

      <ProblemTypeVideo
        videoId={draft.videoId}
        onChange={(videoId) => {
          setDraft((current) => ({ ...current, videoId }))
        }}
      />

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={isSaving || uploading}>
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          {submitLabel}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            if (draft.videoId !== null && draft.videoId !== initial.videoId) {
              void discardVideo.mutateAsync(draft.videoId).catch((caught) => {
                logger.error('Could not discard an unsaved explainer video', {
                  message: toErrorMessage(caught),
                })
              })
            }
            onCancel()
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}
